// ABOUTME: Modal for adding a new calendar provider connection to the family.
// ABOUTME: Supports Google/Outlook OAuth flows, CalDAV server credentials, and ICS subscription URLs.

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CloudIcon from '@mui/icons-material/Cloud';
import DnsIcon from '@mui/icons-material/Dns';
import LinkIcon from '@mui/icons-material/Link';
import { CalendarProviderType } from '../../../types/api';
import { calendarAPI } from '../../../services/api';

interface AddCalendarConnectionModalProps {
  open: boolean;
  onClose: () => void;
  onConnectionAdded: () => void;
  /** When provided, the modal opens in edit mode to update CalDAV credentials. */
  editingConnection?: { id: number; displayName?: string; serverUrl?: string };
}

type Step = 'select-provider' | 'caldav-form' | 'ics-form';

const AddCalendarConnectionModal: React.FC<AddCalendarConnectionModalProps> = ({
  open,
  onClose,
  onConnectionAdded,
  editingConnection,
}) => {
  const [step, setStep] = useState<Step>(editingConnection ? 'caldav-form' : 'select-provider');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // CalDAV form state
  const [serverUrl, setServerUrl] = useState(editingConnection?.serverUrl ?? '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState(editingConnection?.displayName ?? '');

  // ICS subscription form state
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [icsDisplayName, setIcsDisplayName] = useState('');

  // Sync form fields when editingConnection changes (e.g. modal reopened with different connection)
  React.useEffect(() => {
    if (open) {
      if (editingConnection) {
        setStep('caldav-form');
        setServerUrl(editingConnection.serverUrl ?? '');
        setDisplayName(editingConnection.displayName ?? '');
        setUsername('');
        setPassword('');
      } else {
        setStep('select-provider');
        setServerUrl('');
        setDisplayName('');
        setUsername('');
        setPassword('');
      }
      setError(undefined);
    }
  }, [open, editingConnection]);

  const handleClose = () => {
    setStep(editingConnection ? 'caldav-form' : 'select-provider');
    setLoading(false);
    setError(undefined);
    setServerUrl(editingConnection?.serverUrl ?? '');
    setUsername('');
    setPassword('');
    setDisplayName(editingConnection?.displayName ?? '');
    setSubscriptionUrl('');
    setIcsDisplayName('');
    onClose();
  };

  const handleOAuthProvider = async (providerType: CalendarProviderType) => {
    setLoading(true);
    setError(undefined);

    try {
      const redirectUri = `${window.location.origin}/calendar/oauth/callback`;
      const result = await calendarAPI.startOAuth(providerType, redirectUri);
      if (result.authorizationUrl) {
        // Store state in sessionStorage for the callback to verify
        sessionStorage.setItem('calendar_oauth_state', result.state);
        sessionStorage.setItem('calendar_oauth_provider', String(providerType));
        window.location.href = result.authorizationUrl;
      } else {
        setError('Failed to start authorization. Provider may not be configured.');
      }
    } catch {
      setError('Failed to start authorization flow.');
    } finally {
      setLoading(false);
    }
  };

  const handleCalDAVSubmit = async () => {
    if (!serverUrl.trim() || !username.trim() || !password.trim()) {
      setError('Server URL, username, and password are required.');
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      if (editingConnection) {
        await calendarAPI.updateCalDavCredentials(editingConnection.id, {
          serverUrl: serverUrl.trim(),
          username: username.trim(),
          password: password.trim(),
          displayName: displayName.trim() || undefined,
        });
      } else {
        await calendarAPI.createConnection({
          providerType: CalendarProviderType.CalDAV,
          displayName: displayName.trim() || 'CalDAV Calendar',
          serverUrl: serverUrl.trim(),
          username: username.trim(),
          password: password.trim(),
        });
      }
      onConnectionAdded();
      handleClose();
    } catch {
      setError(editingConnection
        ? 'Failed to update credentials. Please verify the server URL and credentials.'
        : 'Failed to connect. Please verify the server URL and credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleIcsSubmit = async () => {
    if (!subscriptionUrl.trim()) {
      setError('Calendar feed URL is required.');
      return;
    }

    // Normalize webcal:// to https:// for the API call
    let normalizedUrl = subscriptionUrl.trim();
    if (normalizedUrl.startsWith('webcal://')) {
      normalizedUrl = normalizedUrl.replace('webcal://', 'https://');
    }

    setLoading(true);
    setError(undefined);

    try {
      await calendarAPI.createConnection({
        providerType: CalendarProviderType.IcsSubscription,
        displayName: icsDisplayName.trim() || undefined,
        serverUrl: normalizedUrl,
      });
      onConnectionAdded();
      handleClose();
    } catch {
      setError('Failed to subscribe. Please verify the calendar feed URL is valid and publicly accessible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="add-calendar-connection-modal"
    >
      <DialogTitle>{editingConnection ? 'Update CalDAV Credentials' : 'Add Calendar Source'}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {step === 'select-provider' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Choose a calendar provider to connect:
            </Typography>
            <List>
              <ListItemButton
                onClick={() => handleOAuthProvider(CalendarProviderType.Google)}
                disabled={loading}
                data-testid="provider-google"
              >
                <ListItemIcon><GoogleIcon /></ListItemIcon>
                <ListItemText primary="Google Calendar" secondary="Connect via Google account" />
                {loading && <CircularProgress size={20} />}
              </ListItemButton>

              <ListItemButton
                onClick={() => handleOAuthProvider(CalendarProviderType.Outlook)}
                disabled={loading}
                data-testid="provider-outlook"
              >
                <ListItemIcon><CloudIcon /></ListItemIcon>
                <ListItemText primary="Outlook Calendar" secondary="Connect via Microsoft account" />
                {loading && <CircularProgress size={20} />}
              </ListItemButton>

              <ListItemButton
                onClick={() => setStep('caldav-form')}
                disabled={loading}
                data-testid="provider-caldav"
              >
                <ListItemIcon><DnsIcon /></ListItemIcon>
                <ListItemText primary="CalDAV Server" secondary="Radicale, Bakal, Nextcloud, etc." />
              </ListItemButton>

              <ListItemButton
                onClick={() => setStep('ics-form')}
                disabled={loading}
                data-testid="provider-ics"
              >
                <ListItemIcon><LinkIcon /></ListItemIcon>
                <ListItemText primary="Calendar Subscription" secondary="iCal/webcal feed URL (read-only)" />
              </ListItemButton>
            </List>
          </Box>
        )}

        {step === 'caldav-form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter your CalDAV server credentials:
            </Typography>
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., School Calendar"
              fullWidth
              size="small"
            />
            <TextField
              label="Server URL"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              placeholder="https://caldav.example.com"
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              fullWidth
              required
              size="small"
            />
          </Box>
        )}

        {step === 'ics-form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter a public iCal/webcal feed URL to subscribe:
            </Typography>
            <TextField
              label="Display Name"
              value={icsDisplayName}
              onChange={(e) => setIcsDisplayName(e.target.value)}
              placeholder="e.g., School Events"
              fullWidth
              size="small"
            />
            <TextField
              label="Calendar Feed URL"
              value={subscriptionUrl}
              onChange={(e) => setSubscriptionUrl(e.target.value)}
              placeholder="https://example.com/calendar.ics or webcal://..."
              fullWidth
              required
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {step === 'caldav-form' && (
          <Button
            onClick={handleCalDAVSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Connect'}
          </Button>
        )}
        {step === 'ics-form' && (
          <Button
            onClick={handleIcsSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Subscribe'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCalendarConnectionModal;
